import { useState, useEffect } from 'react'
import { Histogram } from '../stats/Histogram'
import { GameStats } from '../../lib/localStorage'
import { shareStatus } from '../../lib/share'
import { getUserStats } from '../../lib/api'
import { BaseModal } from './BaseModal'
import {
  STATISTICS_TITLE,
  GUESS_DISTRIBUTION_TEXT,
  NEXT_HAND_TEXT,
  SHARE_TEXT,
  TOTAL_TRIES_TEXT,
  SUCCESS_RATE_TEXT,
  AVERAGE_GUESSES_TEXT,
  CURRENT_STREAK_TEXT,
  BEST_STREAK_TEXT,
} from '../../constants/strings'

type Props = {
  isOpen: boolean
  handleClose: () => void
  guesses: string[]
  gameStats: GameStats
  isGameLost: boolean
  isGameWon: boolean
  handleShare: () => void
  handleNextGame: () => void
  isNextGameDisabled?: boolean
  username: string
}

const StatItem = ({
  label,
  value,
}: {
  label: string
  value: string | number
}) => {
  return (
    <div className="items-center justify-center m-1 w-1/4 dark:text-white">
      <div className="text-3xl font-bold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  )
}

export const StatsModal = ({
  isOpen,
  handleClose,
  guesses,
  gameStats,
  isGameLost,
  isGameWon,
  handleShare,
  handleNextGame,
  isNextGameDisabled = false,
  username,
}: Props) => {
  const [serverStats, setServerStats] = useState<{
    gamesPlayed: number
    gamesWon: number
    totalGuesses: number
  } | null>(null)

  useEffect(() => {
    if (isOpen && username) {
      getUserStats(username).then(setServerStats).catch(() => {})
    }
  }, [isOpen, username])

  const displayStats = serverStats
    ? {
        totalGames: serverStats.gamesPlayed,
        successRate:
          serverStats.gamesPlayed > 0
            ? Math.round((serverStats.gamesWon / serverStats.gamesPlayed) * 100)
            : 0,
        averageGuesses:
          serverStats.gamesWon > 0
            ? (serverStats.totalGuesses / serverStats.gamesWon).toFixed(1)
            : '-',
      }
    : {
        totalGames: gameStats.totalGames,
        successRate: gameStats.successRate,
        averageGuesses: '-',
      }

  return (
    <BaseModal
      title={STATISTICS_TITLE}
      isOpen={isOpen}
      handleClose={handleClose}
    >
      <div className="flex justify-center my-2">
        <StatItem label={TOTAL_TRIES_TEXT} value={displayStats.totalGames} />
        <StatItem label={SUCCESS_RATE_TEXT} value={`${displayStats.successRate}%`} />
        <StatItem label={AVERAGE_GUESSES_TEXT} value={displayStats.averageGuesses} />
        <StatItem label={CURRENT_STREAK_TEXT} value={gameStats.currentStreak} />
        <StatItem label={BEST_STREAK_TEXT} value={gameStats.bestStreak} />
      </div>
      {gameStats.totalGames > 0 && (
        <>
          <h4 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
            {GUESS_DISTRIBUTION_TEXT}
          </h4>
          <Histogram gameStats={gameStats} />
        </>
      )}
      {(isGameLost || isGameWon) && (
        <div className="mt-5 sm:mt-6 grid grid-cols-2 gap-3 dark:text-white">
          <button
            type="button"
            disabled={isNextGameDisabled}
            className="mt-2 w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm disabled:opacity-50"
            onClick={() => {
              handleNextGame()
              handleClose()
            }}
          >
            {isNextGameDisabled ? '同步中...' : NEXT_HAND_TEXT}
          </button>
          <button
            type="button"
            className="mt-2 w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
            onClick={() => {
              shareStatus(guesses, isGameLost)
              handleShare()
            }}
          >
            {SHARE_TEXT}
          </button>
        </div>
      )}
    </BaseModal>
  )
}
